"use client";

import type {
  ClientConfig,
  ContentResponse,
  ProvidersResponse,
  SessionListResponse,
  SessionResponse,
  SessionCreate,
  SessionUpdate,
  RecordCreate,
  SessionRecord,
  FolderResponse,
  FolderCreate,
  FolderUpdate,
  UserPreferences,
  CustomInstructionsResponse,
  MemoriesListResponse,
  CustomQueryResponse,
  CustomQueryCreate,
  CustomQueryUpdate,
  FriendsListResponse,
  FriendRequestResponse,
  ShareSessionRequest,
  SharedInboxResponse,
  ShareLinkCreateRequest,
  ShareLinkCreateResponse,
  SessionShareLinkCreateRequest,
  SessionShareLinkCreateResponse,
  BatchRequest,
  AttachmentUploadResponse,
  FolderContext,
  FolderMemoryResponse,
  ChatRequest,
  BackgroundStartResponse,
  BackgroundStatusResponse,
  DeepResearchBriefResponse,
  DeepResearchBriefJobResponse,
  DeepResearchActiveResponse,
  DeepResearchStartRequest,
  DeepResearchJobStatus,
  CorpusDocumentResponse,
  ChatResponse,
  SummaryResponse,
  TTSRequest,
} from "./types";
import { getAuthState, refreshTokens } from "./auth";

// =============================================================================
// Configuration
// =============================================================================

const API_BASE = process.env.NEXT_PUBLIC_API_BASE?.trim() || "";

// =============================================================================
// Fetch Wrapper with Auth & 401 Retry
// =============================================================================

/** Returns auth headers (Authorization + X-Device-Key or X-DEK) from current auth state.
 *  v2 users send X-DEK; v1 users send X-Device-Key. Never both. */
export function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = { "X-Client-Modes": "modes-v2" };
  const auth = getAuthState();
  if (auth) {
    headers["Authorization"] = `Bearer ${auth.accessToken}`;
    if (auth.schemeVersion === "v2" && auth.dek) {
      headers["X-DEK"] = auth.dek;
    } else if (auth.derivedKey) {
      headers["X-Device-Key"] = auth.derivedKey;
    }
  }
  return headers;
}

function buildHeaders(extra?: Record<string, string>): Record<string, string> {
  return {
    "Content-Type": "application/json",
    // Platform tag — read by server middleware to surface "ios" vs
    // "web" in the request log.
    "X-Client-Platform": "web",
    ...getAuthHeaders(),
    ...extra,
  };
}

async function apiFetchCore<T>(
  path: string,
  init: RequestInit | undefined,
  timeoutMs: number,
  includeContentType: boolean,
): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  const doFetch = async (): Promise<Response> => {
    const headers = includeContentType ? buildHeaders() : getAuthHeaders();
    return fetch(`${API_BASE}${path}`, {
      ...init,
      signal: controller.signal,
      headers: {
        ...headers,
        ...(init?.headers as Record<string, string> | undefined),
      },
    });
  };

  try {
    let response = await doFetch();

    // Auto-retry on 401 by refreshing tokens
    if (response.status === 401) {
      const refreshed = await refreshTokens();
      if (refreshed) {
        response = await doFetch();
      }
    }

    if (!response.ok) {
      const text = await response.text();
      let message = text || `Request failed (${response.status})`;

      try {
        const body: unknown = JSON.parse(text);
        if (
          typeof body === "object" &&
          body !== null &&
          "detail" in body &&
          typeof body.detail === "string" &&
          body.detail.length > 0
        ) {
          message = body.detail;
        }
      } catch {
        // Non-JSON error responses retain their raw response text.
      }

      const error = new Error(message) as Error & { status?: number };
      error.status = response.status;
      throw error;
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new Error(`Request timed out after ${timeoutMs}ms`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

async function apiFetch<T>(
  path: string,
  init?: RequestInit,
  timeoutMs = 30_000
): Promise<T> {
  return apiFetchCore(path, init, timeoutMs, true);
}

/** apiFetch variant that does not set Content-Type (for multipart uploads) */
async function apiFetchRaw<T>(
  path: string,
  init?: RequestInit,
  timeoutMs = 60_000
): Promise<T> {
  return apiFetchCore(path, init, timeoutMs, false);
}

// =============================================================================
// Health
// =============================================================================

export function fetchHealth(): Promise<Record<string, unknown>> {
  return apiFetch("/health", undefined, 5_000);
}

// =============================================================================
// Providers
// =============================================================================

export function fetchProviders(): Promise<ProvidersResponse> {
  return apiFetch("/providers");
}

// Server-driven client behavior config (v1.6). Unauthenticated; carries no
// user data. Clients cache it and fail safe to shipped defaults when it is
// unreachable. See web/app/lib/clientConfig.ts + server/client_config.py.
export function fetchClientConfig(): Promise<ClientConfig> {
  return apiFetch("/client-config");
}

// Server-delivered prose and labels. Clients retain shipped defaults so this
// endpoint is always an optional override, never a render dependency.
export function fetchContent(): Promise<ContentResponse> {
  return apiFetch("/content");
}

// =============================================================================
// Sessions
// =============================================================================

export function fetchSessions(
  offset = 0,
  limit = 1000
): Promise<SessionListResponse> {
  return apiFetch(`/sessions?offset=${offset}&limit=${limit}`);
}

export function fetchSession(id: string): Promise<SessionResponse> {
  return apiFetch(`/sessions/${id}`);
}

export function searchSessions(query: string): Promise<import("./types").SearchResponse> {
  return apiFetch(`/sessions/search?q=${encodeURIComponent(query)}`);
}

export function createSession(data: SessionCreate): Promise<SessionResponse> {
  return apiFetch("/sessions", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateSession(
  id: string,
  data: SessionUpdate
): Promise<SessionResponse> {
  return apiFetch(`/sessions/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deleteSession(id: string): Promise<void> {
  return apiFetch(`/sessions/${id}`, { method: "DELETE" });
}

export function addRecord(
  sessionId: string,
  record: RecordCreate
): Promise<SessionRecord> {
  return apiFetch(`/sessions/${sessionId}/records`, {
    method: "POST",
    body: JSON.stringify(record),
  });
}

export function patchRecord(
  sessionId: string,
  recordId: string,
  body: {
    providers?: Record<string, import("./types").ProviderResult>;
    judge_verdict?: import("./types").JudgeResponse | null;
  }
): Promise<SessionRecord> {
  return apiFetch(`/sessions/${sessionId}/records/${recordId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

// Delete a single record from a session. Used by the Discard-after-Stop
// flow: when the user clicks Stop and decides the partial output isn't
// worth keeping, the Discard button (which briefly replaces Send) calls
// this to delete the just-saved record. The server cascade-deletes the
// session in the same atomic write if this was the last record; clients
// read `session_deleted` to mirror the cascade in local state instantly.
export function deleteRecord(
  sessionId: string,
  recordId: string
): Promise<{ ok: boolean; session_id: string; record_id: string; session_deleted: boolean }> {
  return apiFetch(`/sessions/${sessionId}/records/${recordId}`, {
    method: "DELETE",
  });
}

// =============================================================================
// Folders
// =============================================================================

export function fetchFolders(): Promise<FolderResponse[]> {
  return apiFetch("/sessions/folders");
}

export function createFolder(data: FolderCreate): Promise<FolderResponse> {
  return apiFetch("/sessions/folders", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateFolder(
  id: string,
  data: FolderUpdate
): Promise<FolderResponse> {
  return apiFetch(`/sessions/folders/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function reorderFolders(orderedIds: string[]): Promise<FolderResponse[]> {
  return apiFetch("/sessions/folders/reorder", {
    method: "PUT",
    body: JSON.stringify({ order: orderedIds }),
  });
}

export function deleteFolder(id: string): Promise<void> {
  return apiFetch(`/sessions/folders/${id}`, { method: "DELETE" });
}

// =============================================================================
// Preferences
// =============================================================================

export function fetchPreferences(): Promise<UserPreferences> {
  return apiFetch("/preferences");
}

export function updatePreferences(
  prefs: UserPreferences
): Promise<UserPreferences> {
  return apiFetch("/preferences", {
    method: "PUT",
    body: JSON.stringify(prefs),
  });
}

export function updateCustomInstructions(
  text: string
): Promise<CustomInstructionsResponse> {
  return apiFetch("/preferences/custom-instructions", {
    method: "POST",
    body: JSON.stringify({ text }),
  });
}

export function deleteCustomInstructionItem(
  id: string
): Promise<CustomInstructionsResponse> {
  return apiFetch(`/preferences/custom-instructions/items/${id}`, {
    method: "DELETE",
  });
}

export function clearCustomInstructionsAlert(): Promise<{ ok: boolean }> {
  return apiFetch("/preferences/custom-instructions/alert", { method: "DELETE" });
}

// =============================================================================
// Account
// =============================================================================

/** Update the signed-in user's display name. Server trims and rejects an
 *  empty or >100-char name with HTTP 400. Returns the saved name. */
export function updateAccountName(name: string): Promise<{ name: string }> {
  return apiFetch("/account/name", {
    method: "PUT",
    body: JSON.stringify({ name }),
  });
}

/** Fetch the signed-in user's current display name. Returns `{ name: "" }`
 *  when unset. Used to refresh the Account section for sessions established
 *  before the account was named (the name otherwise only arrives via the
 *  login / token-refresh response). */
export function getAccountName(): Promise<{ name: string }> {
  return apiFetch("/account/name", { method: "GET" });
}

// =============================================================================
// Memories
// =============================================================================

export function fetchMemories(): Promise<MemoriesListResponse> {
  return apiFetch("/memories");
}

export function deleteMemory(id: string): Promise<void> {
  return apiFetch(`/memories/${id}`, { method: "DELETE" });
}

export function deleteAllMemories(): Promise<void> {
  return apiFetch("/memories", { method: "DELETE" });
}

// =============================================================================
// Custom Queries
// =============================================================================

export function fetchCustomQueries(): Promise<CustomQueryResponse[]> {
  return apiFetch("/custom-queries");
}

export function createCustomQuery(
  data: CustomQueryCreate
): Promise<CustomQueryResponse> {
  return apiFetch("/custom-queries", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateCustomQuery(
  id: string,
  data: CustomQueryUpdate
): Promise<CustomQueryResponse> {
  return apiFetch(`/custom-queries/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deleteCustomQuery(id: string): Promise<void> {
  return apiFetch(`/custom-queries/${id}`, { method: "DELETE" });
}

/** Atomically reorder custom queries on the server. Mirrors `PUT /sessions/folders/reorder`. */
export function reorderCustomQueries(
  orderedIds: string[]
): Promise<CustomQueryResponse[]> {
  return apiFetch("/custom-queries/reorder", {
    method: "PUT",
    body: JSON.stringify({ order: orderedIds }),
  });
}

/**
 * Drain any staged results from headless scheduled custom-query runs into the
 * user's history. Call on app open / foreground; cheap to call unconditionally
 * (returns `{ created: [] }` when nothing is pending). When `created` is
 * non-empty, the caller should refresh the session list so the new
 * scheduled-result sessions appear.
 */
export function drainScheduled(): Promise<{
  created: Array<{ session_id: string; title: string }>;
}> {
  return apiFetch("/scheduled/drain", { method: "POST" });
}

// =============================================================================
// Friends
// =============================================================================

export function fetchFriends(): Promise<FriendsListResponse> {
  return apiFetch("/friends");
}

export function sendFriendRequest(
  email: string
): Promise<FriendRequestResponse> {
  return apiFetch("/friends/request", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export function acceptFriendRequest(
  id: string
): Promise<FriendRequestResponse> {
  return apiFetch(`/friends/${id}/accept`, { method: "POST" });
}

export function declineFriendRequest(
  id: string
): Promise<FriendRequestResponse> {
  return apiFetch(`/friends/${id}/decline`, { method: "POST" });
}

export function removeFriend(id: string): Promise<void> {
  return apiFetch(`/friends/${id}`, { method: "DELETE" });
}

// =============================================================================
// Sharing
// =============================================================================

export function shareSession(
  sessionId: string,
  data: ShareSessionRequest
): Promise<void> {
  return apiFetch(`/sessions/${sessionId}/share`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function unshareSession(
  sessionId: string,
  email: string
): Promise<void> {
  return apiFetch(`/sessions/${sessionId}/share/${email}`, {
    method: "DELETE",
  });
}

export function leaveSharedSession(sessionId: string): Promise<void> {
  return apiFetch(`/sessions/${sessionId}/leave`, { method: "POST" });
}

export function fetchSharedInbox(): Promise<SharedInboxResponse> {
  return apiFetch("/shared/inbox");
}

export function markSharedSeen(sessionId: string): Promise<void> {
  return apiFetch(`/shared/${sessionId}/seen`, { method: "POST" });
}

// Create a public, single-turn share link for one chat turn. Mirrors the
// iOS `ShareService.createShare` payload. The server clamps non-admin
// callers to a 24h non-permanent link regardless of `permanent` /
// `expiry_days`; admin callers are honored. The returned `expires_at` is
// authoritative (null = never expires) — callers must display that, not
// the requested lifetime.
export function createShareLink(
  body: ShareLinkCreateRequest
): Promise<ShareLinkCreateResponse> {
  return apiFetch("/shares", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// Create a public, whole-session share link. Unlike `createShareLink`
// (single latest turn), this snapshots the ENTIRE saved session
// server-side — every query, follow-up, provider output, and judge
// run — into a multi-turn `trioai.co/s/<token>` share page. The server
// reads the session from the caller's store by `session_id`, so no
// per-record payload is assembled client-side. Same lifetime clamping
// as `/shares`: non-admin callers are forced to a 24h non-permanent
// link; admin callers are honored. The returned `expires_at` is
// authoritative (null = never expires).
export function createSessionShareLink(
  body: SessionShareLinkCreateRequest
): Promise<SessionShareLinkCreateResponse> {
  return apiFetch("/shares/session", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// =============================================================================
// Batch Operations
// =============================================================================

export function batchSessions(data: BatchRequest): Promise<void> {
  return apiFetch("/sessions/batch", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// =============================================================================
// Chat (Background Jobs)
// =============================================================================

export function startBackgroundJob(
  chatRequest: ChatRequest
): Promise<BackgroundStartResponse> {
  // 30s timeout. The endpoint is "just queue a job", but Python's
  // asyncio.create_task() runs coroutines up to their first await point
  // synchronously, so any sync prep inside the runner tasks (inject system
  // prompt, apply provider prompts, attachment filtering, memory load) eats
  // into the time before the handler can return the job_id. 10s was too
  // tight and routinely tripped for pro-mode kickoffs, leaving the client
  // with "timed out" errors while the server quietly completed the work.
  return apiFetch(
    "/chat/background",
    {
      method: "POST",
      body: JSON.stringify(chatRequest),
    },
    30_000
  );
}

export function pollBackgroundJob(
  jobId: string
): Promise<BackgroundStatusResponse> {
  return apiFetch(`/chat/background/${jobId}`, undefined, 120_000);
}

// Deep Research owns its record server-side. These calls deliberately do not
// share the ordinary /chat/background lifecycle or client-side record writes.
export function draftDeepResearchBrief(question: string): Promise<DeepResearchBriefResponse> {
  return apiFetch("/chat/deep-research/brief", {
    method: "POST",
    body: JSON.stringify({ question }),
  });
}

export function startDeepResearchBrief(question: string): Promise<{ brief_job_id: string }> {
  return apiFetch("/chat/deep-research/brief/start", {
    method: "POST",
    body: JSON.stringify({ question }),
  });
}

export function pollDeepResearchBrief(briefJobId: string): Promise<DeepResearchBriefJobResponse> {
  return apiFetch(`/chat/deep-research/brief/${briefJobId}`, undefined, 30_000);
}

export function fetchActiveDeepResearch(): Promise<DeepResearchActiveResponse> {
  return apiFetch("/chat/deep-research/active");
}

export function startDeepResearch(data: DeepResearchStartRequest): Promise<BackgroundStartResponse> {
  return apiFetch("/chat/deep-research", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function cancelDeepResearch(jobId: string): Promise<BackgroundStartResponse> {
  return apiFetch(`/chat/deep-research/${jobId}/cancel`, { method: "POST" });
}

export function pollDeepResearch(jobId: string): Promise<DeepResearchJobStatus> {
  return apiFetch(`/chat/background/${jobId}`, undefined, 120_000);
}

/** Fetches a cited corpus document only after the reader is opened. */
export function fetchCorpusDocument(url: string): Promise<CorpusDocumentResponse> {
  return apiFetch(url, undefined, 120_000);
}

// =============================================================================
// Chat (SSE Stream) — delegates to sse.ts for actual streaming
// =============================================================================

export function sendChatStream(
  chatRequest: ChatRequest
): Promise<ChatResponse> {
  return apiFetch(
    "/chat/stream",
    {
      method: "POST",
      body: JSON.stringify(chatRequest),
    },
    120_000
  );
}

// =============================================================================
// TTS — read-aloud playback
// =============================================================================

// One-shot synthesis: POST /tts → audio/mpeg. The server checks its
// per-user cache (sha256(text|voice|model|context)[:16]) first — if the
// same (text, voice, model, context) tuple was synthesized before (by
// either iOS or web), the cached MP3 is returned with no OpenAI cost.
// See design/web_tts_read_aloud_plan.md for the iOS↔web sync rationale.
//
// The optional `signal` lets the caller (useTTSPlayer) abort early —
// used by the cache-probe path that races /tts against a short timeout
// and falls back to /tts/stream-text on abort. Note: server-side, /tts
// uses asyncio.shield around the synth task (see
// server/tts_endpoints.py:172), so an abort here does NOT cancel
// synthesis on the server. The synth continues detached and writes to
// cache, warming it for the next request. That's the documented
// "save-on-cancel" behavior.
//
// Returns a Blob the caller can either `URL.createObjectURL()` for an
// <audio> element or feed to the Web Audio API. We bypass the standard
// JSON-parsing apiFetch helper since the response body is binary mp3.
export async function synthesizeTTS(
  request: TTSRequest,
  signal?: AbortSignal,
): Promise<Blob> {
  const headers = {
    "Content-Type": "application/json",
    "X-Client-Platform": "web",
    ...getAuthHeaders(),
  };
  // Default 120s timeout — long TTS responses (10K+ chars) can take
  // 30-90s of synthesis time on a cold cache. Default 30s would cancel
  // mid-synth. Caller may pass a tighter signal for the probe path.
  const internalController = new AbortController();
  const timer = setTimeout(() => internalController.abort(), 120_000);
  // Combine the caller's signal with our internal timeout so the fetch
  // aborts on whichever fires first.
  const onCallerAbort = () => internalController.abort();
  signal?.addEventListener("abort", onCallerAbort, { once: true });
  try {
    let response = await fetch(`${API_BASE}/tts`, {
      method: "POST",
      headers,
      body: JSON.stringify(request),
      signal: internalController.signal,
    });
    if (response.status === 401) {
      const refreshed = await refreshTokens();
      if (refreshed) {
        response = await fetch(`${API_BASE}/tts`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Client-Platform": "web",
            ...getAuthHeaders(),
          },
          body: JSON.stringify(request),
          signal: internalController.signal,
        });
      }
    }
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `TTS request failed (${response.status})`);
    }
    return await response.blob();
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onCallerAbort);
  }
}

// Cache-only lookup: POST /tts/cache-check → audio/mpeg bytes on hit,
// 404 on miss. Never synthesizes, never bills, never burns the rate-
// limit bucket. Mirrors the same (text, voice, model, context) tuple
// that /tts and /tts/stream-text use, so a cache entry from any
// client (iOS or web) is reachable here.
//
// Used by useTTSPlayer to check for an existing cached entry BEFORE
// falling through to /tts/stream-text. Lets us avoid paying for
// duplicate OpenAI synthesis when iOS already played the same text.
// Returns null on miss; throws on auth/server errors.
export async function cacheCheckTTS(request: TTSRequest): Promise<Blob | null> {
  const headers = {
    "Content-Type": "application/json",
    "X-Client-Platform": "web",
    ...getAuthHeaders(),
  };
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 10_000);
  try {
    let response = await fetch(`${API_BASE}/tts/cache-check`, {
      method: "POST",
      headers,
      body: JSON.stringify(request),
      signal: controller.signal,
    });
    if (response.status === 401) {
      const refreshed = await refreshTokens();
      if (refreshed) {
        response = await fetch(`${API_BASE}/tts/cache-check`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Client-Platform": "web",
            ...getAuthHeaders(),
          },
          body: JSON.stringify(request),
          signal: controller.signal,
        });
      }
    }
    if (response.status === 404) {
      return null; // cache miss — caller falls through to /tts/stream-text
    }
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `Cache check failed (${response.status})`);
    }
    return await response.blob();
  } finally {
    clearTimeout(timer);
  }
}

// Cache-status probe: POST /tts/cache-status → {hit: boolean}. Tiny
// JSON response, never returns audio bytes. Drives the per-card
// play-button "free vs paid" indicator (solid icon when cached,
// hollow when uncached). Server-side this is a stat()-only lookup,
// no synthesis side effects.
export async function cacheStatusTTS(request: TTSRequest): Promise<boolean> {
  const headers = {
    "Content-Type": "application/json",
    "X-Client-Platform": "web",
    ...getAuthHeaders(),
  };
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 5_000);
  try {
    let response = await fetch(`${API_BASE}/tts/cache-status`, {
      method: "POST",
      headers,
      body: JSON.stringify(request),
      signal: controller.signal,
    });
    if (response.status === 401) {
      const refreshed = await refreshTokens();
      if (refreshed) {
        response = await fetch(`${API_BASE}/tts/cache-status`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Client-Platform": "web",
            ...getAuthHeaders(),
          },
          body: JSON.stringify(request),
          signal: controller.signal,
        });
      }
    }
    if (!response.ok) return false; // any failure → treat as miss
    const json = await response.json();
    return json?.hit === true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

// Streaming synthesis: POST /tts/stream-text → audio/mpeg chunks
// streamed as bytes arrive. First chunk lands in ~1-2s vs /tts's
// "block on full synthesis" which can be 30-90s for long text.
// Returns the raw Response so the caller can stream `.body` into a
// MediaSource buffer for true streaming playback. Does NOT write to
// the server-side cache (cf. /tts which does); see
// design/web_parity_pending.md "TTS read-aloud" entry for the cost
// model around this asymmetry.
export async function streamTTSText(
  request: TTSRequest,
  signal?: AbortSignal,
): Promise<Response> {
  const headers = {
    "Content-Type": "application/json",
    "X-Client-Platform": "web",
    ...getAuthHeaders(),
  };
  const internalController = new AbortController();
  const timer = setTimeout(() => internalController.abort(), 600_000);
  const onCallerAbort = () => internalController.abort();
  signal?.addEventListener("abort", onCallerAbort, { once: true });
  try {
    let response = await fetch(`${API_BASE}/tts/stream-text`, {
      method: "POST",
      headers,
      body: JSON.stringify(request),
      signal: internalController.signal,
    });
    if (response.status === 401) {
      const refreshed = await refreshTokens();
      if (refreshed) {
        response = await fetch(`${API_BASE}/tts/stream-text`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Client-Platform": "web",
            ...getAuthHeaders(),
          },
          body: JSON.stringify(request),
          signal: internalController.signal,
        });
      }
    }
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `TTS stream request failed (${response.status})`);
    }
    // Note: timer + abort listener intentionally NOT cleared here —
    // the caller consumes response.body as a stream, so abort needs
    // to remain wired up until the stream finishes. The caller is
    // responsible for clearing them via the returned signal's lifecycle.
    return response;
  } catch (err) {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onCallerAbort);
    throw err;
  }
  // No finally — the response stream lives on past this function.
}

// =============================================================================
// Attachments
// =============================================================================

export function uploadAttachments(
  files: File[]
): Promise<AttachmentUploadResponse> {
  const formData = new FormData();
  for (const file of files) {
    formData.append("files", file);
  }
  // 5-min timeout (vs apiFetchRaw's 60s default). 32 MB attachments at
  // typical home upload speeds (~5 Mbps) take 50+ seconds on the wire
  // alone, before server-side Ghostscript shrink (5-15s extra). The 60s
  // default cancels the upload mid-flight on slow networks.
  return apiFetchRaw(
    "/attachments",
    { method: "POST", body: formData },
    300_000
  );
}

// =============================================================================
// Folder Context
// =============================================================================

export function fetchFolderContext(folderId: string): Promise<FolderContext> {
  return apiFetch(`/folders/${folderId}/context`);
}

export function fetchFolderMemory(folderId: string): Promise<FolderMemoryResponse> {
  return apiFetch(`/folders/${folderId}/memory`);
}

export function deleteFolderMemoryItem(folderId: string, itemId: string): Promise<{ ok: boolean }> {
  return apiFetch(`/folders/${folderId}/memory/${itemId}`, { method: "DELETE" });
}

export function fetchFolderContextVersion(
  folderId: string,
  versionId: string
): Promise<FolderContext> {
  return apiFetch(`/folders/${folderId}/context/${versionId}`);
}

export function generateFolderContext(folderId: string): Promise<void> {
  return apiFetch(`/folders/${folderId}/generate-context`, { method: "POST" });
}

// =============================================================================
// Summarize
// =============================================================================

const SUMMARIZE_PROMPT_TIMEOUT_MS = 20_000;

export function summarizePrompt(
  prompt: string,
  opts?: {
    traceId?: string;
    attachmentIds?: string[];
    customQueryPrompt?: string;
  }
): Promise<SummaryResponse> {
  const body: Record<string, unknown> = { prompt };
  if (opts?.traceId) body.trace_id = opts.traceId;
  if (opts?.attachmentIds && opts.attachmentIds.length > 0) {
    body.attachment_ids = opts.attachmentIds;
  }
  if (opts?.customQueryPrompt) body.custom_query_prompt = opts.customQueryPrompt;
  return apiFetch(
    "/summarize",
    {
      method: "POST",
      body: JSON.stringify(body),
    },
    SUMMARIZE_PROMPT_TIMEOUT_MS
  );
}

// =============================================================================
// Judge
// =============================================================================

// streamJudge lives in sse.ts (SSE consumer matching iOS ChatViewModel).
// The blocking judgeResponses() function is gone — server is SSE-only post-Phase 3.1.

// Cancel an in-flight chat stream server-side. discard=false keeps partial
// output (Stop button); discard=true is a hint that the client will drop
// the record (Discard button). Either way the server halts provider tasks.
export function cancelChatStream(
  streamId: string,
  discard: boolean
): Promise<{ ok: boolean; cancelled: boolean; buffer_status: string }> {
  return apiFetch(
    `/chat/stream/${streamId}/cancel`,
    {
      method: "POST",
      body: JSON.stringify({ discard }),
    },
    5_000
  );
}

export function deleteJudgeVerdict(
  sessionId: string,
  recordIndex: number
): Promise<{ status: string }> {
  return apiFetch(`/judge/${sessionId}/${recordIndex}`, { method: "DELETE" });
}

// =============================================================================
// Auto-mode classifier
// =============================================================================

export function classifyChat(
  request: import("./types").ClassifyRequest
): Promise<import("./types").ClassifyResponse> {
  return apiFetch(
    "/chat/classify",
    {
      method: "POST",
      body: JSON.stringify(request),
    },
    15_000
  );
}

// =============================================================================
// Admin
// =============================================================================

export function fetchAdminUsers(): Promise<any> {
  return apiFetch("/admin/users?anonymize=false");
}

export function fetchAdminInvites(): Promise<any> {
  return apiFetch("/admin/invites");
}

export function createAdminInvite(
  email: string,
  name: string,
  sendToSelf: boolean = false,
): Promise<any> {
  return apiFetch("/admin/invites", {
    method: "POST",
    body: JSON.stringify({ email, name, send_to_self: sendToSelf }),
  });
}

export function revokeAdminInvite(code: string): Promise<any> {
  return apiFetch(`/admin/invites/${code}`, { method: "DELETE" });
}

export function resendAdminInvite(
  code: string,
  sendToSelf: boolean = false,
): Promise<any> {
  return apiFetch(`/admin/invites/${code}/resend`, {
    method: "POST",
    body: JSON.stringify({ send_to_self: sendToSelf }),
  });
}

export function purgeAdminUser(email: string): Promise<any> {
  return apiFetch(`/admin/users/${encodeURIComponent(email)}/purge`, {
    method: "POST",
  });
}

export function disableAdminUser(email: string): Promise<any> {
  return apiFetch(`/admin/users/${encodeURIComponent(email)}/disable`, {
    method: "POST",
  });
}

export function enableAdminUser(email: string): Promise<any> {
  return apiFetch(`/admin/users/${encodeURIComponent(email)}/enable`, {
    method: "POST",
  });
}

export function fetchAdminUsageSummary(): Promise<any> {
  return apiFetch("/admin/usage/summary");
}

export function fetchAdminUserUsage(email: string): Promise<any> {
  return apiFetch(`/admin/usage/${encodeURIComponent(email)}`);
}

export interface JudgeStatsDemarcation {
  key: string;
  label: string;
  since_ts: string | null;
  default: boolean;
}

export function fetchUserJudgeStats(since?: string | null): Promise<{
  total_runs: number;
  per_provider: Record<
    "anthropic" | "gemini" | "openai",
    {
      wins: number;
      judged: number;
      self_wins: number;
      self_and_judged: number;
      // Score aggregation (panel-mode entries only — legacy single-
      // rotation runs don't carry their raw scores in the audit log).
      // null when no panel-scored runs in the selected window.
      avg_score_received: number | null;
      avg_self_score: number | null;
    }
  >;
  // OAI bias headline numbers for the selected window. All nullable
  // when no panel runs landed in the window. trio_oai_self_penalty is
  // the currently-deployed Δ correction constant — not a measurement,
  // a configuration.
  bias_metrics?: {
    oai_premium_on_oai: number | null;
    openai_premium_vs_others: number | null;
    gemini_premium_on_gemini?: number | null;
    trio_oai_self_penalty: number;
    trio_gemini_self_penalty?: number;
    rows?: { id: string; label: string; value: string }[];
    // Count of panel-mode verdicts contributing to the cells matrix.
    // Surfaces dataset size: stays identical across windows that all
    // enclose the same panel-era subset (panel scoring started
    // 2026-05-05), which is why bias means don't change between them.
    panel_runs?: number;
  };
  since?: string;
  since_ts?: string | null;
}> {
  const qs = since ? `?since=${encodeURIComponent(since)}` : "";
  return apiFetch(`/usage/me/judge-stats${qs}`, { method: "GET" });
}

export function fetchJudgeStatsDemarcations(): Promise<{
  demarcations: JudgeStatsDemarcation[];
}> {
  return apiFetch("/usage/me/judge-stats/demarcations", { method: "GET" });
}

export function fetchJudgeStatsExcludes(): Promise<{ excludes: string[] }> {
  return apiFetch("/usage/me/judge-stats/excludes", { method: "GET" });
}

export function setJudgeStatsExclude(prompt: string, excluded: boolean): Promise<{
  ok: boolean;
  excluded: boolean;
}> {
  return apiFetch("/usage/me/judge-stats/exclude", {
    method: "POST",
    body: JSON.stringify({ prompt, exclude: excluded }),
  });
}

// Per-model sub-row inside a usage row. Absent on pre-overhaul buckets,
// so callers treat the whole array as optional / possibly empty.
export interface UsageByModelRow {
  model: string;
  requests: number;
  web_search_count?: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
}

// One (provider, mode) usage row.
export interface UsageRow {
  provider: string;
  mode: string;
  requests: number;
  web_search_count?: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  by_model?: UsageByModelRow[];
}

// One section/mode rollup for the collapsible usage UI. `section` ∈
// fast / thinking / pro / deep_research / judging / audio / other.
export interface UsageSectionRow {
  section: string;
  requests: number;
  web_search_count?: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
}

export function fetchUserUsageSummary(): Promise<{
  lifetime: {
    total_cost_usd: number;
    total_requests: number;
    total_input_tokens: number;
    total_output_tokens: number;
  };
  current_month: string;
  months: Array<{
    month: string;
    total_cost_usd: number;
    total_requests: number;
    total_input_tokens: number;
    total_output_tokens: number;
    rows: UsageRow[];
    sections?: UsageSectionRow[];
  }>;
  cost_note: string;
}> {
  return apiFetch("/usage/me/summary", { method: "GET" });
}

export function fetchAdminNotifications(): Promise<any> {
  return apiFetch("/admin/notifications");
}

export function markAdminNotificationsRead(): Promise<any> {
  return apiFetch("/admin/notifications/read", { method: "POST" });
}

export function fetchAdminBadgeCounts(): Promise<import("./types").AdminBadgeCounts> {
  return apiFetch("/admin/badge-counts");
}

export function markAdminTabRead(
  tab: "feedback" | "exports" | "usage"
): Promise<void> {
  return apiFetch("/admin/notifications/read-type", {
    method: "POST",
    body: JSON.stringify({ tab }),
  });
}

export function submitFeedback(message: string): Promise<any> {
  return apiFetch("/feedback", {
    method: "POST",
    body: JSON.stringify({ message }),
  });
}

export function fetchAdminFeedback(): Promise<any> {
  return apiFetch("/admin/feedback");
}

// Per-admin dismissed-IDs (cross-device-synced filter for the Admin →
// Exports / Feedback lists). Mirrors the iOS APIClient calls in
// `AdminConsoleView.fetchDismissedIds` / `dismissAdminIds`.
export function fetchAdminDismissedIds(): Promise<{
  exports: string[];
  feedback: string[];
}> {
  return apiFetch("/admin/dismissed-ids");
}

export function addAdminDismissedIds(
  type: "exports" | "feedback",
  ids: string[]
): Promise<{ exports: string[]; feedback: string[] }> {
  return apiFetch("/admin/dismissed-ids", {
    method: "POST",
    body: JSON.stringify({ type, ids }),
  });
}

// =============================================================================
// Admin — Pricing Proposals
// =============================================================================

// One pre-ordered row for the admin Pricing tab. The server returns
// `display[]` already sorted: chat tiers mode-major, then judging, then
// other, then audio/TTS last. Render in array order.
export interface PricingDisplayRow {
  section: "chat" | "judging" | "other" | "audio";
  group: string;
  provider: string;
  model: string;
  label: string;
  input: number;
  output: number;
  unit: "tokens" | "characters";
  priced: boolean;
}

export function fetchAdminPricing(): Promise<{
  display?: PricingDisplayRow[];
  model_rates?: Record<string, { input: number; output: number }>;
  rates: Record<string, Record<string, { input: number; output: number }>>;
  version: number;
  effective_from: string | null;
  using_fallback?: boolean;
  model_count?: number;
  notes?: Record<string, string>;
}> {
  return apiFetch("/admin/pricing");
}

export function fetchPricingProposals(): Promise<import("./types").PricingProposalsResponse> {
  return apiFetch("/admin/pricing-proposals");
}

export function applyPricingProposal(
  id: string,
  approvedItems: import("./types").PricingProposalApprovedItem[]
): Promise<any> {
  return apiFetch(`/admin/pricing-proposals/${encodeURIComponent(id)}/apply`, {
    method: "POST",
    body: JSON.stringify({ approved_items: approvedItems }),
  });
}

export function rejectPricingProposal(id: string): Promise<any> {
  return apiFetch(`/admin/pricing-proposals/${encodeURIComponent(id)}/reject`, {
    method: "POST",
  });
}

// =============================================================================
// Admin — Model Proposals (daily check_new_models.py output)
// =============================================================================

export function fetchModelProposals(): Promise<import("./types").ModelProposalsResponse> {
  return apiFetch("/admin/model-proposals");
}

export function dismissModelProposal(id: string): Promise<any> {
  return apiFetch(`/admin/model-proposals/${encodeURIComponent(id)}/dismiss`, {
    method: "POST",
  });
}

// =============================================================================
// Export for Admin
// =============================================================================

export function exportForAdmin(
  sessionIds: string[],
  clientDiagnostics?: Array<{ ts: string; tag: string; msg: string }>
): Promise<{ status: string; exported_sessions: number; exported_at: string; client_diag_entries?: number }> {
  const body: Record<string, unknown> = { session_ids: sessionIds };
  if (clientDiagnostics && clientDiagnostics.length > 0) {
    body.client_diagnostics = clientDiagnostics;
  }
  return apiFetch("/export-for-admin", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function fetchAllSessions(
  offset = 0,
  limit = 1000
): Promise<SessionListResponse> {
  return apiFetch(`/sessions?offset=${offset}&limit=${limit}`);
}
